import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const plans = [
  {
    name: "Standard",
    tagline: "For city sedans & hatchbacks",
    price: "999",
    period: "/month",
    features: [
      "8 exterior waterless washes",
      "Monthly deep-clean check",
      "Photo-verified service",
      "Pause anytime",
    ],
    cta: "Choose Standard",
    highlighted: false,
  },
  {
    name: "Premium",
    tagline: "Most popular for SUVs",
    price: "1,799",
    period: "/month",
    features: [
      "12 exterior + 2 interior washes",
      "Bi-weekly deep-clean check",
      "Priority scheduling",
      "Free re-wash guarantee",
      "Damage insurance up to ₹50k",
    ],
    cta: "Choose Premium",
    highlighted: true,
  },
  {
    name: "Luxury",
    tagline: "For premium & exotic cars",
    price: "3,499",
    period: "/month",
    features: [
      "Unlimited washes",
      "Weekly deep-clean check",
      "Dedicated senior Zypper",
      "Polish + sanitisation included",
      "Damage insurance up to ₹2L",
    ],
    cta: "Choose Luxury",
    highlighted: false,
  },
];

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Pricing() {
  return (
    <section
      id="plans"
      data-testid="subscription-pricing"
      className="relative py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FF5A00] mb-4">
            Plans
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">
            One subscription. Zero excuses.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            All plans include doorstep service, photo verification, and a free
            re-wash guarantee. Bangalore launch pricing — limited slots.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5">
          {plans.map((p, i) => {
            const card = (
              <div
                className={`relative h-full rounded-2xl p-8 md:p-10 transition-all duration-300 ${
                  p.highlighted
                    ? "bg-gradient-to-b from-zinc-900 to-black border border-[#FF5A00]/30 shadow-[0_0_60px_-25px_#FF5A00]"
                    : "bg-zinc-950/40 border border-white/5 hover:border-white/15"
                }`}
              >
                {p.highlighted && (
                  <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF5A00] text-white text-xs font-medium">
                    <Sparkles size={12} /> Most popular
                  </div>
                )}
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                  {p.name}
                </div>
                <div className="mt-2 text-sm text-zinc-400">{p.tagline}</div>
                <div className="mt-8 flex items-baseline gap-1">
                  <span className="text-zinc-500 text-xl">₹</span>
                  <span className="font-heading text-5xl md:text-6xl font-semibold tracking-tighter">
                    {p.price}
                  </span>
                  <span className="text-zinc-500 text-sm">{p.period}</span>
                </div>
                <div className="mt-6 h-px bg-white/5" />
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-sm text-zinc-300"
                    >
                      <Check
                        size={16}
                        className="text-[#FF5A00] mt-0.5 shrink-0"
                        strokeWidth={2.5}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  onClick={() => scrollTo("#waitlist")}
                  className={`mt-10 w-full h-12 rounded-full font-medium ${
                    p.highlighted
                      ? "bg-[#FF5A00] hover:bg-[#FF7A33] text-white"
                      : "bg-white text-black hover:bg-zinc-200"
                  }`}
                >
                  {p.cta}
                </Button>
              </div>
            );

            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`pricing-card-${p.name.toLowerCase()}`}
                className={p.highlighted ? "md:-translate-y-3" : ""}
              >
                {card}
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-10">
          GST included • Cancel or pause anytime • Refer a friend, both get ₹500
        </p>
      </div>
    </section>
  );
}
